import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useAppSelector } from "../../store/hooks";

const mono =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

const Wrap = styled.div`
  min-height: calc(100vh - 64px);
  background: #f0f0f0;
  padding: 1.5rem 1.25rem 2.5rem;
  font-family: ${mono};
`;

const Column = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.5rem;
  font-size: clamp(1.65rem, 4vw, 2.15rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const Muted = styled.p`
  margin: 0.35rem 0 0;
  color: #5a5a5a;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const HelpJump = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.35rem;
  width: 1.35rem;
  height: 1.35rem;
  border: 2px solid #111;
  background: #fff;
  color: #111;
  font-weight: 800;
  text-decoration: none;
  font-size: 0.95rem;
  line-height: 1;
  box-shadow: 2px 2px 0 #111;
  &:hover {
    transform: translate(-1px, -1px);
    box-shadow: 3px 3px 0 #111;
  }
`;

const Card = styled.section`
  background: #ffffff;
  border: 2px solid #111;
  box-shadow: 8px 8px 0 #111;
  padding: 1.25rem;
  margin-top: 1.75rem;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 1rem;
`;

const StepNum = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  background: #111;
  color: #fff;
  font-weight: 800;
  font-size: 1.15rem;
`;

const StepHeading = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: 0.02em;
`;

const Body = styled.div`
  color: #222;
  font-size: 0.95rem;
  line-height: 1.65;

  p {
    margin: 0 0 0.85rem;
  }
  p:last-child {
    margin-bottom: 0;
  }

  strong {
    font-weight: 800;
  }

  code {
    font-family: inherit;
    background: #ececec;
    padding: 0.1rem 0.35rem;
    border: 1px solid #ccc;
  }

  a {
    color: #111;
    font-weight: 700;
  }

  ul {
    margin: 0 0 0.85rem;
    padding-left: 1.25rem;
  }

  li {
    margin: 0.35rem 0;
  }

  li:last-child {
    margin-bottom: 0;
  }
`;

const EpdMock = styled.div`
  margin-top: 1rem;
  border: 2px solid #111;
  background: #fafafa;
  padding: 1.25rem 1rem;
  text-align: center;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  color: #333;
`;

const WifiCardBox = styled.div`
  margin-top: 1rem;
  border: 2px solid #111;
  background: #fff;
  padding: 1rem 1rem 0.85rem;
`;

const WifiRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 2px solid #eee;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const WifiLabel = styled.span`
  color: #5a5a5a;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const WifiValue = styled.span`
  font-weight: 800;
  font-size: 1rem;
  word-break: break-all;
  text-align: right;
  flex: 1;
`;

const CopyBtn = styled.button`
  flex-shrink: 0;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.35rem 0.55rem;
  border: 2px solid #111;
  background: #fff;
  cursor: pointer;
  box-shadow: 2px 2px 0 #111;
  &:hover {
    transform: translate(-1px, -1px);
    box-shadow: 3px 3px 0 #111;
  }
`;

const QrPlaceholder = styled.div`
  margin-top: 1rem;
  border: 2px dashed #111;
  background: #fafafa;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1rem;
  color: #555;
  font-size: 0.9rem;
`;

const NoPhoneCallout = styled.div`
  background: #fff;
  border: 1px dashed #232323;
  padding: 1rem;
  margin: 1rem 0;
`;

const NoPhoneDetails = styled.details`
  margin: 0;

  summary {
    cursor: pointer;
    font-weight: 700;
    font-family: ${mono};
    list-style: none;
    &::-webkit-details-marker {
      display: none;
    }
  }

  > ${Body} {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid #e0e0e0;
  }
`;

const RecoveryCard = styled.section`
  background: #fffbd6;
  border: 2px solid #111;
  box-shadow: 8px 8px 0 #111;
  padding: 1.25rem;
  margin-top: 1.75rem;
`;

const RecoverySectionHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 1rem;
`;

const RecoveryLabel = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.55rem;
  background: #fff;
  border: 2px solid #111;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1;
`;

const RecoveryHeading = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: 0.02em;
`;

const EpdCornerDiagram = styled.pre`
  margin: 1rem 0 0;
  border: 2px solid #111;
  background: #fafafa;
  padding: 1rem;
  font-family: inherit;
  font-size: 0.72rem;
  line-height: 1.35;
  white-space: pre;
  overflow-x: auto;
  color: #222;
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const BtnPrimary = styled(Link)`
  display: inline-block;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 800;
  padding: 0.65rem 1rem;
  border: 2px solid #111;
  background: #ffd24a;
  color: #111;
  text-decoration: none;
  box-shadow: 4px 4px 0 #111;
  &:hover {
    transform: translate(-1px, -1px);
    box-shadow: 6px 6px 0 #111;
  }
`;

const BtnSecondary = styled(Link)`
  display: inline-block;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 700;
  padding: 0.65rem 1rem;
  border: 2px solid #111;
  background: #fff;
  color: #111;
  text-decoration: none;
  box-shadow: 3px 3px 0 #111;
  &:hover {
    transform: translate(-1px, -1px);
    box-shadow: 4px 4px 0 #111;
  }
`;

const FootNote = styled.p`
  margin: 2rem 0 0;
  font-size: 0.88rem;
  color: #666;
  a {
    color: #111;
    font-weight: 700;
  }
`;

const TroubleSection = styled.section`
  margin-top: 2.5rem;
  padding-top: 0.5rem;
`;

const TroubleTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.15rem;
  font-weight: 800;
`;

const DetailBlock = styled.details`
  border: 2px solid #111;
  background: #fff;
  margin-bottom: 0.65rem;
  box-shadow: 4px 4px 0 #111;

  &[open] {
    box-shadow: 6px 6px 0 #111;
  }

  summary {
    cursor: pointer;
    font-weight: 700;
    padding: 0.75rem 1rem;
    list-style: none;
    &::-webkit-details-marker {
      display: none;
    }
  }

  > div {
    padding: 0 1rem 1rem;
    color: #333;
    font-size: 0.92rem;
    line-height: 1.6;
    border-top: 2px solid #eee;
  }

  code {
    font-family: inherit;
    background: #ececec;
    padding: 0.1rem 0.35rem;
    border: 1px solid #ccc;
  }
`;

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }, [value]);

  return (
    <WifiRow>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem", flex: 1, minWidth: 0 }}>
        <WifiLabel>{label}</WifiLabel>
        <WifiValue>{value}</WifiValue>
      </div>
      <CopyBtn type="button" onClick={onCopy}>
        {copied ? "Copied" : "Copy"}
      </CopyBtn>
    </WifiRow>
  );
}

const AotdIrlSetup: React.FC = () => {
  const auth = useAppSelector((s) => s.auth);
  const isLoggedIn = auth.bootstrapStatus === "ready" && Boolean(auth.token);
  const loginHref = "/login?return=/aotd-irl/my";

  return (
    <Wrap>
      <Column>
        <HeroTitle>Set up your AOTD-IRL frame</HeroTitle>
        <Muted>from box to art in a few minutes</Muted>
        <Muted style={{ marginTop: "0.5rem" }}>
          5 steps · ~3 minutes · works with any wifi network
          <HelpJump href="#troubleshooting" title="Troubleshooting">
            ?
          </HelpJump>
        </Muted>

        <Card aria-labelledby="step-1-title">
          <SectionHead>
            <StepNum>1</StepNum>
            <StepHeading id="step-1-title">Plug it in</StepHeading>
          </SectionHead>
          <Body>
            <p>
              Power the frame with the included cable. It lights up and, after a short pause, the e-paper
              panel wakes and shows the setup screen (about 10 seconds).
            </p>
            <EpdMock aria-hidden>
              <pre style={{ margin: 0, fontFamily: "inherit", whiteSpace: "pre-wrap" }}>
                [ AOTD IRL — connect to set up ]
              </pre>
            </EpdMock>
          </Body>
        </Card>

        <Card aria-labelledby="step-2-title">
          <SectionHead>
            <StepNum>2</StepNum>
            <StepHeading id="step-2-title">Connect your phone to the frame</StepHeading>
          </SectionHead>
          <Body>
            <p>
              The frame creates a temporary Wi-Fi network. On your phone, join that network. The name is{" "}
              <strong>AOTD-Setup</strong> and the password is <strong>aotd-setup</strong>.
            </p>
            <WifiCardBox>
              <CopyField label="SSID" value="AOTD-Setup" />
              <CopyField label="Password" value="aotd-setup" />
            </WifiCardBox>
            <p style={{ marginTop: "1rem" }}>
              After you connect, your phone may open the setup page automatically. If it doesn&apos;t, open a
              browser to <code>http://192.168.4.1</code>.
            </p>
          </Body>
        </Card>

        <NoPhoneCallout aria-label="No phone handy">
          <NoPhoneDetails>
            <summary>Don&apos;t have a phone handy?</summary>
            <Body>
              <p>
                Any device that can join wifi and open a browser works — laptops, tablets, smart TVs with
                browsers, even another computer. The QR on your sticker just encodes the wifi credentials, so
                you can also type them in by hand.
              </p>
              <ul>
                <li>
                  <strong>macOS</strong> — wifi menu in the top bar → connect to <strong>AOTD-Setup</strong>{" "}
                  (password <strong>aotd-setup</strong>). The captive portal usually opens automatically. If not,
                  open Safari and go to <code>http://192.168.4.1</code>.
                </li>
                <li>
                  <strong>Windows 10 / 11</strong> — taskbar wifi icon → <strong>AOTD-Setup</strong> → enter{" "}
                  <strong>aotd-setup</strong> → captive portal opens. Manual: any browser →{" "}
                  <code>http://192.168.4.1</code>.
                </li>
                <li>
                  <strong>Linux</strong> — NetworkManager → <strong>AOTD-Setup</strong>. Manual: any browser →{" "}
                  <code>http://192.168.4.1</code>.
                </li>
                <li>
                  <strong>Tablets (iPad, Android)</strong> — same as phones.
                </li>
              </ul>
              <p>
                The credentials are always the same: SSID <strong>AOTD-Setup</strong>, password{" "}
                <strong>aotd-setup</strong>. They&apos;re also printed on the back of your frame.
              </p>
              <WifiCardBox>
                <CopyField label="SSID" value="AOTD-Setup" />
                <CopyField label="Password" value="aotd-setup" />
              </WifiCardBox>
            </Body>
          </NoPhoneDetails>
        </NoPhoneCallout>

        <Card aria-labelledby="step-3-title">
          <SectionHead>
            <StepNum>3</StepNum>
            <StepHeading id="step-3-title">Enter your home wifi</StepHeading>
          </SectionHead>
          <Body>
            <p>
              In the captive portal, type your home Wi-Fi name and password, then submit. The frame saves
              them and reboots onto your network.
            </p>
            <p>
              If the captive portal doesn&apos;t appear, open <code>http://192.168.4.1</code> manually in any
              browser.
            </p>
          </Body>
        </Card>

        <Card aria-labelledby="step-4-title">
          <SectionHead>
            <StepNum>4</StepNum>
            <StepHeading id="step-4-title">Scan the QR on the frame</StepHeading>
          </SectionHead>
          <Body>
            <p>
              After the frame reconnects (roughly 30 seconds), the screen shows a QR code and short
              instructions. Use your phone camera, point it at the QR, and open the link.
            </p>
            <p>
              Can&apos;t scan? Enter the device ID shown on the screen at{" "}
              <Link to="/aotd-irl/claim">/aotd-irl/claim</Link>.
            </p>
            <QrPlaceholder>QR code displayed on e-paper</QrPlaceholder>
          </Body>
        </Card>

        <Card aria-labelledby="step-5-title">
          <SectionHead>
            <StepNum>5</StepNum>
            <StepHeading id="step-5-title">Your frame is yours</StepHeading>
          </SectionHead>
          <Body>
            <p>
              Once claimed, the frame pulls your Art of the Day on its next wake cycle (within about an hour,
              or sooner if you press the wake button on the back). After that it refreshes on schedule,
              including around sunrise each day.
            </p>
            <CtaRow>
              {isLoggedIn ? (
                <BtnPrimary to="/aotd-irl/my">Manage your frames</BtnPrimary>
              ) : (
                <BtnPrimary to={loginHref}>Sign in to manage</BtnPrimary>
              )}
              <BtnSecondary to="/aotd-irl/buy">Get another frame →</BtnSecondary>
            </CtaRow>
          </Body>
        </Card>

        <RecoveryCard aria-labelledby="recovery-title">
          <RecoverySectionHead>
            <RecoveryLabel>Recovery</RecoveryLabel>
            <div>
              <RecoveryHeading id="recovery-title">If your frame loses internet</RecoveryHeading>
              <Muted>things happen — wifi changes, routers reboot, networks rotate passwords</Muted>
            </div>
          </RecoverySectionHead>
          <Body>
            <p>
              Frames keep showing the last AOTD they downloaded even when they can&apos;t reach the internet —
              so the art doesn&apos;t disappear.
            </p>
            <p>
              If your frame can&apos;t sync on its next wake, a small <code>wifi setup needed</code> message
              will appear in the bottom-right corner of the displayed art.
            </p>
            <p>
              To fix it: scan the wifi QR sticker on the back of your frame again (or use any wifi-capable
              device), connect to <strong>AOTD-Setup</strong>, and enter your wifi credentials in the captive
              portal.
            </p>
            <p>
              <strong>Anyone can do this</strong> — it doesn&apos;t have to be the original owner. Wifi
              credentials live locally on the frame, but ownership lives on n450s.com tied to the
              device&apos;s permanent ID. Re-onboarding wifi never resets the claim.
            </p>
            <p>The frame returns to normal on its next sync (within ~1 hour).</p>
            <EpdCornerDiagram aria-hidden>{`+---------------------------+
|                           |
|         (your art)        |
|                           |
|              [!] wifi     |
|              setup needed |
+---------------------------+`}</EpdCornerDiagram>
          </Body>
        </RecoveryCard>

        <TroubleSection id="troubleshooting">
          <TroubleTitle>Troubleshooting</TroubleTitle>

          <DetailBlock>
            <summary>I don&apos;t see the AOTD-Setup wifi network</summary>
            <div>
              Wait about 30 seconds after plugging in. If it still doesn&apos;t appear, hold the reset
              button on the back for 5 seconds, then try again.
            </div>
          </DetailBlock>

          <DetailBlock>
            <summary>The captive portal doesn&apos;t open</summary>
            <div>
              Open <code>http://192.168.4.1</code> in any browser while connected to the frame&apos;s
              network.
            </div>
          </DetailBlock>

          <DetailBlock>
            <summary>I entered the wrong wifi password</summary>
            <div>
              Power-cycle the frame. After ~30 seconds the <code>AOTD-Setup</code> network will be available
              again and you can enter the correct credentials. No data loss — the frame remembers which user
              owns it.
            </div>
          </DetailBlock>

          <DetailBlock>
            <summary>My frame says &quot;wifi setup needed&quot; in the corner</summary>
            <div>
              This means your frame can&apos;t reach the internet on its current wifi. Scan the QR sticker on
              the back (any phone, tablet, or laptop works), connect to <code>AOTD-Setup</code>, and re-enter
              your wifi credentials. The frame will resume normal updates on its next sync. Your ownership
              and claim are unaffected — this is a wifi-only fix.
            </div>
          </DetailBlock>

          <DetailBlock>
            <summary>I lost my QR sticker</summary>
            <div>
              The QR is just a wifi credential URI:{" "}
              <code>WIFI:T:WPA;S:AOTD-Setup;P:aotd-setup;;</code>. Any free QR generator can recreate it. Or
              skip the QR entirely — connect to the <code>AOTD-Setup</code> wifi network manually with password{" "}
              <code>aotd-setup</code>, then open <code>http://192.168.4.1</code> in any browser.
            </div>
          </DetailBlock>

          <DetailBlock>
            <summary>The QR code never appears</summary>
            <div>
              Confirm your home network has internet access. Check the LED on the back for connection
              status.
            </div>
          </DetailBlock>

          <DetailBlock>
            <summary>I scanned the QR but got &quot;invalid or expired pairing token&quot;</summary>
            <div>
              Scan the QR again. Pairing tokens rotate periodically (about every 24 hours).
            </div>
          </DetailBlock>

          <DetailBlock>
            <summary>My frame says claimed but isn&apos;t displaying art</summary>
            <div>
              Wait up to one hour for the next sync, or press the wake button on the back to pull sooner.
            </div>
          </DetailBlock>

          <DetailBlock>
            <summary>I want to give my frame to someone else</summary>
            <div>
              Unclaim it from{" "}
              <Link to="/aotd-irl/my">/aotd-irl/my</Link> and have the new owner scan the
              QR (or claim with the device ID).
            </div>
          </DetailBlock>
        </TroubleSection>

        <FootNote>
          Need more help? Email{" "}
          <a href="mailto:contact@n450s.com">contact@n450s.com</a>.
        </FootNote>
      </Column>
    </Wrap>
  );
};

export default AotdIrlSetup;
